// ============================================================================
// 📦 InventarioCMDB.js — Inventário de Itens (Chips + Rastreadores) / CMDB
// ============================================================================
// Catalogação de ativos físicos da central. Tema claro, accent teal + indigo.
// Multitenant: as rotas /api/inventario/* já filtram por tenant.
//
// NOVIDADES desta versão (1→5):
//  • Catálogo DINÂMICO: tipos e status vêm do backend (/tipos, /status). O front
//    resolve ícone (string → componente react-icons) e cor por dado, não por slug.
//  • Modal MULTI-TENANT (admin_geral): lista os tenants e abre os itens de cada
//    um com paginação 25/25 — tudo read-only.
//  • Modal de GESTÃO DE CATÁLOGO (admin_geral): CRUD global de tipos, status e
//    categorias, respeitando registros protegidos (canônicos).
//  • IMPORT/EXPORT com SheetJS: botões Modelo, Exportar e Importar (admin_geral
//    exporta/importa escolhendo o tenant de destino).
//  • Etiquetas de TENANT em todas as listagens (apenas p/ admin_geral).
//  • Upload de NF real (multipart) → Google Drive.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaMicrochip, FaSatelliteDish, FaBoxOpen, FaPlus, FaSearch, FaFileImport,
  FaLink, FaUnlink, FaTrashAlt, FaPen, FaTimes, FaExchangeAlt, FaPaperclip,
  FaProjectDiagram, FaBell, FaGhost, FaLayerGroup, FaClipboardCheck,
  FaGlobeAmericas, FaHeartbeat, FaHistory, FaCar, FaUserShield, FaFileInvoiceDollar,
  FaExclamationTriangle, FaCheckCircle, FaSyncAlt, FaCube, FaSpinner, FaArrowRight,
  // novos
  FaServer, FaHdd, FaPlug, FaWifi, FaBatteryFull, FaKey, FaTabletAlt, FaMobileAlt,
  FaBroadcastTower, FaMapMarkerAlt, FaCogs, FaSimCard, FaBuilding, FaTags,
  FaChevronLeft, FaChevronRight, FaSlidersH, FaSave, FaFileExport, FaFileDownload,
  FaLock, FaToggleOn, FaToggleOff, FaListUl, FaUser, FaCheck,
} from 'react-icons/fa';
import './InventarioCMDB.css';

/* ============================================================================
 * Resolução de ícones dinâmicos (string do banco → componente)
 * ========================================================================== */
const ICON_MAP = {
  FaMicrochip, FaSatelliteDish, FaCube, FaBoxOpen, FaCar, FaServer, FaHdd, FaPlug,
  FaWifi, FaBatteryFull, FaKey, FaTabletAlt, FaMobileAlt, FaBroadcastTower,
  FaMapMarkerAlt, FaCogs, FaSimCard,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);
const resolveIcon = (name) => ICON_MAP[name] || FaBoxOpen;

// Endpoint do módulo de usuários (routesUsers). A rota /selecao retorna os
// usuários ativos do tenant: { id, nome, email, perfil }.
// ⚠️ Ajuste aqui se o mount do routesUsers no seu app for diferente (ex.: '/api/users').
const API_USUARIOS = '/api/usuarios';

/* ============================================================================
 * 💬 Sistema de diálogos (confirm/prompt) — promise-based, sem nativos do browser
 * Substitui window.confirm / window.prompt por modais estilizados e acessíveis.
 * Uso: const { confirm, prompt } = useDialogs();
 *      if (await confirm({ titulo, mensagem, perigo })) { ... }
 *      const valor = await prompt({ titulo, placeholder });  // null se cancelar
 * ========================================================================== */
const DialogCtx = React.createContext({
  confirm: async () => false,
  prompt: async () => null,
});
const useDialogs = () => useContext(DialogCtx);

function ConfirmDialog({ titulo, mensagem, confirmLabel, cancelLabel, perigo, onResolve }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="inv-modal-overlay" onClick={() => onResolve(false)}>
      <div className="inv-modal sm inv-confirm" onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onResolve(false); }}>
        <div className={`inv-confirm-ic ${perigo ? 'perigo' : ''}`}>
          {perigo ? <FaExclamationTriangle /> : <FaCheckCircle />}
        </div>
        <h3>{titulo}</h3>
        {mensagem && <p>{mensagem}</p>}
        <div className="inv-modal-foot">
          <button className="inv-btn-ghost" onClick={() => onResolve(false)}>{cancelLabel || 'Cancelar'}</button>
          <button ref={ref} className={`inv-btn ${perigo ? 'danger' : ''}`} onClick={() => onResolve(true)}>
            {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InputDialog({ titulo, mensagem, placeholder, inicial, tipo, confirmLabel, obrigatorio, onResolve }) {
  const [val, setVal] = useState(inicial != null ? String(inicial) : '');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const ok = () => {
    const limpo = val.trim();
    if (obrigatorio && !limpo) { ref.current?.focus(); return; }
    onResolve(limpo === '' ? (obrigatorio ? null : '') : limpo);
  };
  return (
    <div className="inv-modal-overlay" onClick={() => onResolve(null)}>
      <div className="inv-modal sm" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3>{titulo}</h3>
          <button className="inv-icon-btn" onClick={() => onResolve(null)}><FaTimes /></button>
        </div>
        <div className="inv-modal-body">
          {mensagem && <p className="inv-dialog-msg">{mensagem}</p>}
          <input
            ref={ref} className="inv-dialog-input" type={tipo || 'text'} value={val}
            placeholder={placeholder || ''}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ok(); if (e.key === 'Escape') onResolve(null); }}
          />
        </div>
        <div className="inv-modal-foot">
          <button className="inv-btn-ghost" onClick={() => onResolve(null)}>Cancelar</button>
          <button className="inv-btn" onClick={ok}>{confirmLabel || 'Confirmar'}</button>
        </div>
      </div>
    </div>
  );
}

function DialogProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null);
  const [inputState, setInputState] = useState(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    setConfirmState({ opts, resolve });
  }), []);
  const prompt = useCallback((opts = {}) => new Promise((resolve) => {
    setInputState({ opts, resolve });
  }), []);

  const resolveConfirm = (v) => { confirmState?.resolve(v); setConfirmState(null); };
  const resolveInput = (v) => { inputState?.resolve(v); setInputState(null); };

  const ctx = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <DialogCtx.Provider value={ctx}>
      {children}
      {confirmState && <ConfirmDialog {...confirmState.opts} onResolve={resolveConfirm} />}
      {inputState && <InputDialog {...inputState.opts} onResolve={resolveInput} />}
    </DialogCtx.Provider>
  );
}

const PAPEL_OPTIONS = [
  { value: 'nenhum', label: 'Nenhum (genérico)' },
  { value: 'chip', label: 'Chip (SIM / M2M)' },
  { value: 'rastreador', label: 'Rastreador (GPS)' },
];

/* ============================================================================
 * Metadados de saúde (fixos — o score vem calculado do backend)
 * ========================================================================== */
const SAUDE_META = {
  otimo:   { label: 'Ótimo',   classe: 'sa-otimo' },
  bom:     { label: 'Bom',     classe: 'sa-bom' },
  atencao: { label: 'Atenção', classe: 'sa-atencao' },
  critico: { label: 'Crítico', classe: 'sa-critico' },
};

const PODE_MANIPULAR = ['admin_geral', 'admin', 'operacional'];

/* ============================================================================
 * Helpers
 * ========================================================================== */
const moeda = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataBR = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const dataHoraBR = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// fundo translúcido a partir de uma cor hex (#rrggbb) — p/ chips dinâmicos
const hexBg = (cor) => (cor && /^#([0-9a-f]{6})$/i.test(cor) ? `${cor}1f` : 'var(--inv-slate-soft)');

// Constrói o índice de metadados a partir das listas do backend
function construirMeta(tipos, status) {
  const tiposBySlug = {};
  (tipos || []).forEach((t) => { tiposBySlug[t.slug] = { ...t, Icone: resolveIcon(t.icone) }; });
  const statusBySlug = {};
  (status || []).forEach((s) => { statusBySlug[s.slug] = { ...s }; });
  return { tipos: tipos || [], status: status || [], tiposBySlug, statusBySlug };
}

const META_VAZIO = { tipos: [], status: [], tiposBySlug: {}, statusBySlug: {} };

// Colunas do modelo/exportação (batem com o que o backend importa)
const COLUNAS_EXPORT = [
  'codigo_patrimonio', 'tipo', 'nome', 'status',
  'iccid', 'numero_linha', 'operadora', 'plano_dados', 'custo_mensal',
  'imei', 'protocolo', 'firmware',
  'fabricante', 'modelo', 'numero_serie',
  'localizacao', 'valor_aquisicao', 'data_aquisicao', 'vida_util_meses',
  'nota_fiscal_numero', 'garantia_ate', 'observacoes',
];

/* ============================================================================
 * Componentes pequenos reutilizáveis
 * ========================================================================== */
function StatusBadge({ meta, status }) {
  const s = meta?.statusBySlug?.[status];
  if (!s) return <span className="inv-badge">{status || '—'}</span>;
  if (s.cor) return <span className="inv-badge" style={{ color: s.cor, background: hexBg(s.cor) }}>{s.label}</span>;
  return <span className={`inv-badge ${s.classe || ''}`}>{s.label}</span>;
}

function TipoChip({ meta, tipo, mini }) {
  const t = meta?.tiposBySlug?.[tipo];
  if (!t) return <span className="inv-tipo-chip">{tipo || '—'}</span>;
  const Icone = t.Icone;
  return (
    <span className="inv-tipo-chip" style={{ color: t.cor, background: hexBg(t.cor) }}>
      <Icone /> {mini ? null : t.label}
    </span>
  );
}

function TenantTag({ nome }) {
  if (!nome) return null;
  return <span className="inv-tenant-tag" title={nome}><FaBuilding /> {nome}</span>;
}

function HealthGauge({ saude, size = 64 }) {
  const score = saude?.score ?? 0;
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className={`inv-gauge ${SAUDE_META[saude?.nivel]?.classe || ''}`} title={SAUDE_META[saude?.nivel]?.label}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} className="inv-gauge-track" strokeWidth="6" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} className="inv-gauge-fill" strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x="50%" y="52%" className="inv-gauge-num" textAnchor="middle" dominantBaseline="middle">{score}</text>
      </svg>
    </div>
  );
}

function KpiCard({ icon, valor, label, accent }) {
  return (
    <div className={`inv-kpi ${accent ? `inv-kpi--${accent}` : ''}`}>
      <div className="inv-kpi-icon">{icon}</div>
      <div className="inv-kpi-body">
        <div className="inv-kpi-valor">{valor}</div>
        <div className="inv-kpi-label">{label}</div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 🕸️ TOPOLOGIA — grafo SVG (layout por PAPEL: chip → rastreador → veículo)
 * ========================================================================== */
function TopologyView({ meta, onAbrirItem }) {
  const [data, setData] = useState({ nodes: [], edges: [] });
  const [pos, setPos] = useState({});
  const [loading, setLoading] = useState(true);
  const dragRef = useRef(null);
  const svgRef = useRef(null);

  const colDoNo = (nd) => {
    const papel = nd.papel || meta.tiposBySlug[nd.tipo]?.papel || 'nenhum';
    if (nd.kind === 'veiculo' || papel === 'veiculo') return 2;
    if (papel === 'rastreador') return 1;
    if (papel === 'chip') return 0;
    return 0;
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/api/inventario/topologia');
      const contadores = {};
      const p = {};
      d.nodes.forEach((nd) => {
        const col = colDoNo(nd);
        contadores[col] = (contadores[col] || 0) + 1;
        p[nd.id] = { x: 130 + col * 260, y: 70 + (contadores[col] - 1) * 96 };
      });
      setData(d);
      setPos(p);
    } catch {
      toast.error('Não foi possível carregar a topologia.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  useEffect(() => { carregar(); }, [carregar]);

  const ptDoEvento = (e) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const vb = svg.viewBox.baseVal;
    const x = ((e.clientX - rect.left) / rect.width) * vb.width;
    const y = ((e.clientY - rect.top) / rect.height) * vb.height;
    return { x, y };
  };
  const onPointerDown = (id) => (e) => {
    e.preventDefault();
    const { x, y } = ptDoEvento(e);
    dragRef.current = { id, dx: x - pos[id].x, dy: y - pos[id].y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { x, y } = ptDoEvento(e);
    const { id, dx, dy } = dragRef.current;
    setPos((prev) => ({ ...prev, [id]: { x: x - dx, y: y - dy } }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const alturaCol = Math.max(...Object.values(
    data.nodes.reduce((acc, nd) => {
      const col = colDoNo(nd);
      acc[col] = (acc[col] || 0) + 1;
      return acc;
    }, { 0: 1 })
  ), 1);
  const altura = Math.max(360, 70 + alturaCol * 96);

  if (loading) return <div className="inv-loading"><FaSpinner className="inv-spin" /> Montando topologia…</div>;
  if (!data.nodes.length) {
    return (
      <div className="inv-empty">
        <FaProjectDiagram className="inv-empty-icon" />
        <p>Sem itens para mapear ainda. Cadastre chips e rastreadores e crie vínculos para ver o grafo de dependências.</p>
      </div>
    );
  }

  return (
    <div className="inv-topo">
      <div className="inv-topo-legenda">
        <span><i className="dot tipo-chip" /> Chip</span>
        <span><i className="dot tipo-rastreador" /> Rastreador</span>
        <span><i className="dot tipo-veiculo" /> Veículo</span>
        <button className="inv-btn-ghost" onClick={carregar}><FaSyncAlt /> Recarregar</button>
      </div>
      <svg
        ref={svgRef}
        className="inv-topo-canvas"
        viewBox={`0 0 760 ${altura}`}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <defs>
          <marker id="seta" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L9,3 L0,6 Z" className="inv-topo-arrowhead" />
          </marker>
        </defs>
        {data.edges.map((ed) => {
          const a = pos[ed.source]; const b = pos[ed.target];
          if (!a || !b) return null;
          const mx = (a.x + b.x) / 2;
          return (
            <g key={ed.id}>
              <path
                d={`M ${a.x + 22} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x - 24} ${b.y}`}
                className="inv-topo-edge" markerEnd="url(#seta)"
              />
              <text x={mx} y={(a.y + b.y) / 2 - 6} className="inv-topo-edge-label" textAnchor="middle">
                {ed.label}
              </text>
            </g>
          );
        })}
        {data.nodes.map((nd) => {
          const p = pos[nd.id]; if (!p) return null;
          const papel = nd.papel || meta.tiposBySlug[nd.tipo]?.papel || 'nenhum';
          const ehVeiculo = nd.kind === 'veiculo' || papel === 'veiculo';
          const classePapel = ehVeiculo ? 'tipo-veiculo' : (papel === 'chip' ? 'tipo-chip' : papel === 'rastreador' ? 'tipo-rastreador' : 'tipo-outro');
          const Icone = ehVeiculo ? FaCar : (meta.tiposBySlug[nd.tipo]?.Icone || FaBoxOpen);
          return (
            <g
              key={nd.id}
              transform={`translate(${p.x}, ${p.y})`}
              className={`inv-topo-node ${classePapel}`}
              onPointerDown={onPointerDown(nd.id)}
              onDoubleClick={() => nd.kind === 'item' && onAbrirItem(nd.rawId)}
            >
              <circle r="22" className="inv-topo-node-bg" />
              <foreignObject x="-12" y="-12" width="24" height="24">
                <div className="inv-topo-node-ic"><Icone /></div>
              </foreignObject>
              <text y="38" className="inv-topo-node-label" textAnchor="middle">{nd.label}</text>
            </g>
          );
        })}
      </svg>
      <p className="inv-topo-dica">Arraste os nós para reorganizar · duplo-clique abre o item</p>
    </div>
  );
}

/* ============================================================================
 * 🚨 ALERTAS
 * ========================================================================== */
function AlertasView({ onAbrirItem, podeManipular }) {
  const [dados, setDados] = useState({ total: 0, alertas: [], porSeveridade: {} });
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/inventario/alertas');
      setDados(data);
    } catch { toast.error('Falha ao carregar alertas.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const dispensar = async (a) => {
    try {
      await api.post('/api/inventario/alertas/dispensar', { item_id: a.item_id, tipo_alerta: a.tipo, dias: 7 });
      toast.success('Alerta dispensado por 7 dias.');
      carregar();
    } catch { toast.error('Não foi possível dispensar.'); }
  };

  if (loading) return <div className="inv-loading"><FaSpinner className="inv-spin" /> Avaliando ativos…</div>;
  if (!dados.total) {
    return <div className="inv-empty"><FaCheckCircle className="inv-empty-icon ok" /><p>Nenhum alerta. Inventário saudável.</p></div>;
  }

  return (
    <div className="inv-alertas">
      <div className="inv-alertas-resumo">
        <span className="sev-alta">{dados.porSeveridade.alta || 0} alta</span>
        <span className="sev-media">{dados.porSeveridade.media || 0} média</span>
        <span className="sev-baixa">{dados.porSeveridade.baixa || 0} baixa</span>
      </div>
      <ul className="inv-alertas-lista">
        {dados.alertas.map((a, i) => (
          <li key={i} className={`inv-alerta sev-${a.severidade}`}>
            <FaExclamationTriangle className="inv-alerta-ic" />
            <div className="inv-alerta-corpo">
              <button className="inv-link" onClick={() => onAbrirItem(a.item_id)}>{a.codigo || `#${a.item_id}`}</button>
              <span className="inv-alerta-msg">{a.mensagem}</span>
            </div>
            {podeManipular && (
              <button className="inv-btn-ghost sm" onClick={() => dispensar(a)}>Dispensar 7d</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================================
 * 👻 RECONCILIAÇÃO
 * ========================================================================== */
function ReconciliacaoView({ onAbrirItem }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/inventario/reconciliacao');
      setDados(data);
    } catch { toast.error('Falha na reconciliação.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return <div className="inv-loading"><FaSpinner className="inv-spin" /> Cruzando com a frota real…</div>;

  return (
    <div className="inv-recon">
      <div className="inv-recon-head">
        <p>Compara o que está catalogado com o que os rastreadores realmente reportam.</p>
        <button className="inv-btn-ghost" onClick={carregar}><FaSyncAlt /> Recalcular</button>
      </div>
      <div className="inv-recon-grid">
        <div className="inv-recon-card fantasma">
          <h4><FaGhost /> Fantasmas <span className="inv-pill">{dados?.fantasmas?.total || 0}</span></h4>
          <p className="inv-recon-desc">Catalogados como em uso, mas sem sinal recente.</p>
          <ul>
            {(dados?.fantasmas?.lista || []).map((f) => (
              <li key={f.id}>
                <button className="inv-link" onClick={() => onAbrirItem(f.id)}>{f.codigo_patrimonio}</button>
                <span className="mono">{f.imei || '—'}</span>
                <span className="muted">{f.ultimo_sinal_em ? dataHoraBR(f.ultimo_sinal_em) : 'nunca reportou'}</span>
              </li>
            ))}
            {!dados?.fantasmas?.total && <li className="inv-recon-vazio">Nenhum fantasma 👌</li>}
          </ul>
        </div>
        <div className="inv-recon-card orfao">
          <h4><FaSatelliteDish /> Órfãos <span className="inv-pill">{dados?.orfaos?.total || 0}</span></h4>
          <p className="inv-recon-desc">Reportando sinal, mas não catalogados no inventário.</p>
          {dados?.reconciliavel === false ? (
            <div className="inv-recon-aviso">
              Mapeamento da tabela de rastreadores precisa de ajuste no backend (constante <code>RECON</code>).
            </div>
          ) : (
            <ul>
              {(dados?.orfaos?.lista || []).map((o, i) => (
                <li key={i}>
                  <span className="mono">{o.imei || '—'}</span>
                  <span className="muted">{o.ultimo_sinal ? dataHoraBR(o.ultimo_sinal) : '—'}</span>
                </li>
              ))}
              {!dados?.orfaos?.total && <li className="inv-recon-vazio">Nenhum órfão 👌</li>}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 📦 KITS
 * ========================================================================== */
function KitsView({ podeManipular }) {
  const { confirm, prompt } = useDialogs();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoNome, setNovoNome] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/inventario/kits');
      setKits(data);
    } catch { toast.error('Falha ao carregar kits.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const criar = async () => {
    if (!novoNome.trim()) return;
    try {
      await api.post('/api/inventario/kits', { nome: novoNome.trim() });
      setNovoNome('');
      toast.success('Kit criado.');
      carregar();
    } catch { toast.error('Falha ao criar kit.'); }
  };

  const implantar = async (kit) => {
    const veiculo = await prompt({
      titulo: 'Implantar kit',
      mensagem: `Informe o veículo de destino para "${kit.nome}".`,
      placeholder: 'ID do veículo (ex.: 42)',
      tipo: 'number',
      confirmLabel: 'Implantar',
      obrigatorio: true,
    });
    const veiculoId = parseInt(veiculo, 10);
    if (!veiculoId) return;
    try {
      const { data } = await api.post(`/api/inventario/kits/${kit.id}/implantar`, { veiculo_id: veiculoId });
      toast.success(`Kit implantado: ${data.itens} itens, ${data.pares} pares vinculados.`);
      carregar();
    } catch (e) { toast.error(e.response?.data?.error || 'Falha ao implantar.'); }
  };

  const excluir = async (kit) => {
    if (!(await confirm({ titulo: 'Excluir kit', mensagem: `Excluir o kit "${kit.nome}"?`, confirmLabel: 'Excluir', perigo: true }))) return;
    try { await api.delete(`/api/inventario/kits/${kit.id}`); carregar(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  if (loading) return <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando kits…</div>;

  return (
    <div className="inv-kits">
      {podeManipular && (
        <div className="inv-kits-novo">
          <input
            placeholder="Nome do novo kit (ex.: Kit Instalação Padrão)"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && criar()}
          />
          <button className="inv-btn" onClick={criar}><FaPlus /> Criar kit</button>
        </div>
      )}
      {!kits.length ? (
        <div className="inv-empty"><FaLayerGroup className="inv-empty-icon" /><p>Nenhum kit montado. Agrupe rastreador + chip para implantar num veículo de uma vez.</p></div>
      ) : (
        <div className="inv-kits-grid">
          {kits.map((k) => (
            <div key={k.id} className="inv-kit-card">
              <div className="inv-kit-head">
                <strong>{k.nome}</strong>
                <span className="inv-badge">{k.status}</span>
              </div>
              <p className="inv-kit-desc">{k.descricao || 'Sem descrição'}</p>
              <div className="inv-kit-meta">
                <span>{k.qtd_itens} item(ns)</span>
                {k.veiculo_id && <span><FaCar /> veículo #{k.veiculo_id}</span>}
              </div>
              {podeManipular && (
                <div className="inv-kit-acoes">
                  {k.status !== 'implantado' && (
                    <button className="inv-btn sm" onClick={() => implantar(k)}><FaCar /> Implantar</button>
                  )}
                  <button className="inv-btn-ghost sm danger" onClick={() => excluir(k)}><FaTrashAlt /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * 📋 STOCKTAKE
 * ========================================================================== */
function StocktakeView({ meta, podeManipular }) {
  const { prompt } = useDialogs();
  const [lista, setLista] = useState([]);
  const [aberto, setAberto] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/inventario/stocktake');
      setLista(data);
    } catch { toast.error('Falha ao carregar.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const iniciar = async () => {
    const titulo = await prompt({
      titulo: 'Novo inventário físico',
      mensagem: 'Dê um nome para esta campanha de conferência.',
      placeholder: 'ex.: Conferência de chips — Julho/2026',
      confirmLabel: 'Iniciar',
      obrigatorio: true,
    });
    if (!titulo) return;
    try { await api.post('/api/inventario/stocktake', { titulo }); carregar(); }
    catch { toast.error('Falha ao iniciar.'); }
  };

  const abrir = async (id) => {
    try { const { data } = await api.get(`/api/inventario/stocktake/${id}`); setAberto(data); }
    catch { toast.error('Falha ao abrir.'); }
  };

  const conferir = async (itemId, status) => {
    try {
      await api.post(`/api/inventario/stocktake/${aberto.id}/conferir`, { item_id: itemId, status_conf: status });
      abrir(aberto.id);
    } catch { toast.error('Falha ao registrar conferência.'); }
  };

  if (loading) return <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando…</div>;

  if (aberto) {
    return (
      <div className="inv-stk-detalhe">
        <button className="inv-btn-ghost" onClick={() => setAberto(null)}>← Voltar</button>
        <h3>{aberto.titulo}</h3>
        <table className="inv-table">
          <thead><tr><th>Patrimônio</th><th>Item</th><th>Tipo</th><th>Conferência</th><th></th></tr></thead>
          <tbody>
            {aberto.itens.map((it) => (
              <tr key={it.id}>
                <td className="mono">{it.codigo_patrimonio}</td>
                <td>{it.nome || '—'}</td>
                <td>{meta.tiposBySlug[it.tipo]?.label || it.tipo}</td>
                <td><span className="inv-badge">{it.status_conf}</span></td>
                <td className="inv-stk-acoes">
                  {podeManipular && (
                    <>
                      <button className="inv-btn-ghost sm ok" onClick={() => conferir(it.item_id, 'conferido')}>OK</button>
                      <button className="inv-btn-ghost sm warn" onClick={() => conferir(it.item_id, 'divergente')}>Diverge</button>
                      <button className="inv-btn-ghost sm danger" onClick={() => conferir(it.item_id, 'nao_encontrado')}>Sumiu</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="inv-stk">
      {podeManipular && (
        <button className="inv-btn" onClick={iniciar}><FaPlus /> Novo inventário físico</button>
      )}
      {!lista.length ? (
        <div className="inv-empty"><FaClipboardCheck className="inv-empty-icon" /><p>Nenhuma campanha de conferência. Inicie uma para auditar os ativos fisicamente.</p></div>
      ) : (
        <table className="inv-table">
          <thead><tr><th>Título</th><th>Status</th><th>Progresso</th><th>Criado</th><th></th></tr></thead>
          <tbody>
            {lista.map((s) => (
              <tr key={s.id}>
                <td>{s.titulo}</td>
                <td><span className="inv-badge">{s.status}</span></td>
                <td>{s.conferidos}/{s.total_itens}</td>
                <td>{dataBR(s.created_at)}</td>
                <td><button className="inv-btn-ghost sm" onClick={() => abrir(s.id)}>Abrir</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================================
 * 🌐 MODAL MULTI-TENANT — lista de tenants + drill-down paginado (25/25)
 * ========================================================================== */
function MultiTenantModal({ meta, onClose }) {
  const [view, setView] = useState('tenants'); // 'tenants' | 'itens'
  const [tenants, setTenants] = useState([]);
  const [geral, setGeral] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(true);

  const [tenant, setTenant] = useState(null);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [loadingItens, setLoadingItens] = useState(false);
  const LIMIT = 25;

  useEffect(() => {
    (async () => {
      try {
        const [{ data: ts }, { data: rs }] = await Promise.all([
          api.get('/api/inventario/global/tenants'),
          api.get('/api/inventario/global/resumo'),
        ]);
        setTenants(ts);
        setGeral(rs?.geral || null);
      } catch { toast.error('Falha ao carregar a visão multi-tenant.'); }
      finally { setLoadingTenants(false); }
    })();
  }, []);

  const carregarItens = useCallback(async (tId, off, q, tipo) => {
    setLoadingItens(true);
    try {
      const params = new URLSearchParams();
      params.set('tenant_id', tId);
      params.set('limit', LIMIT);
      params.set('offset', off);
      if (q) params.set('q', q);
      if (tipo) params.set('tipo', tipo);
      const { data } = await api.get(`/api/inventario/global/itens?${params.toString()}`);
      setItens(data.itens || []);
      setTotal(data.total || 0);
      setOffset(off);
    } catch { toast.error('Falha ao carregar itens do tenant.'); }
    finally { setLoadingItens(false); }
  }, []);

  const abrirTenant = (t) => {
    setTenant(t);
    setView('itens');
    setBusca('');
    setFiltroTipo('');
    carregarItens(t.id, 0, '', '');
  };

  const voltar = () => { setView('tenants'); setTenant(null); setItens([]); };

  // debounce da busca dentro do tenant aberto
  useEffect(() => {
    if (view !== 'itens' || !tenant) return undefined;
    const t = setTimeout(() => carregarItens(tenant.id, 0, busca, filtroTipo), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtroTipo]);

  const totalPaginas = Math.max(1, Math.ceil(total / LIMIT));
  const paginaAtual = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3>
            <FaGlobeAmericas />{' '}
            {view === 'tenants' ? 'Visão Multi-Tenant' : (
              <>
                <button className="inv-link inv-voltar" onClick={voltar}><FaChevronLeft /> Tenants</button>
                {' · '}{tenant?.nome_empresa}
              </>
            )}
          </h3>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="inv-modal-body">
          <div className="inv-global-banner">
            <FaUserShield /> Visão consolidada entre tenants — somente leitura
          </div>

          {view === 'tenants' ? (
            loadingTenants ? (
              <div className="inv-loading"><FaSpinner className="inv-spin" /> Consolidando tenants…</div>
            ) : (
              <>
                <div className="inv-kpis">
                  <KpiCard icon={<FaGlobeAmericas />} valor={geral?.tenants || 0} label="Tenants com inventário" accent="indigo" />
                  <KpiCard icon={<FaCube />} valor={geral?.total || 0} label="Itens (todos)" />
                  <KpiCard icon={<FaFileInvoiceDollar />} valor={moeda(geral?.valor_total)} label="Valor total" accent="teal" />
                </div>
                <div className="inv-tenant-lista">
                  {tenants.map((t) => (
                    <button key={t.id} className="inv-tenant-card" onClick={() => abrirTenant(t)}>
                      <div className="inv-tenant-logo">
                        {t.logo_url ? <img src={t.logo_url} alt={t.nome_empresa} /> : <FaBuilding />}
                      </div>
                      <div className="inv-tenant-info">
                        <strong>{t.nome_empresa}</strong>
                        <span className="muted">{t.slug ? `@${t.slug}` : `#${t.id}`}</span>
                      </div>
                      <div className="inv-tenant-stats">
                        <span><b>{t.total_itens}</b> itens</span>
                        <span className="muted">{moeda(t.valor_total)}</span>
                      </div>
                      <FaChevronRight className="inv-tenant-seta" />
                    </button>
                  ))}
                  {!tenants.length && <div className="inv-empty"><FaBuilding className="inv-empty-icon" /><p>Nenhum tenant com inventário.</p></div>}
                </div>
              </>
            )
          ) : (
            <>
              <div className="inv-toolbar">
                <div className="inv-busca">
                  <FaSearch />
                  <input
                    placeholder="Buscar por patrimônio, IMEI, ICCID, linha, série…"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                  <option value="">Todos os tipos</option>
                  {meta.tipos.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                </select>
              </div>

              {loadingItens ? (
                <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando itens…</div>
              ) : !itens.length ? (
                <div className="inv-empty"><FaBoxOpen className="inv-empty-icon" /><p>Nenhum item para este tenant com os filtros atuais.</p></div>
              ) : (
                <>
                  <div className="inv-tabela-wrap">
                    <table className="inv-table">
                      <thead>
                        <tr><th>Tenant</th><th>Patrimônio</th><th>Tipo</th><th>Identificador</th><th>Status</th><th>Valor</th></tr>
                      </thead>
                      <tbody>
                        {itens.map((it) => {
                          const papel = meta.tiposBySlug[it.tipo]?.papel;
                          return (
                            <tr key={it.id}>
                              <td><TenantTag nome={it.tenant_nome} /></td>
                              <td className="mono">{it.codigo_patrimonio}</td>
                              <td><TipoChip meta={meta} tipo={it.tipo} /></td>
                              <td className="mono inv-ident">{papel === 'chip' ? (it.iccid || '—') : (it.imei || '—')}</td>
                              <td><StatusBadge meta={meta} status={it.status} /></td>
                              <td>{it.valor_aquisicao ? moeda(it.valor_aquisicao) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="inv-paginacao">
                    <button
                      className="inv-btn-ghost sm" disabled={paginaAtual <= 1}
                      onClick={() => carregarItens(tenant.id, offset - LIMIT, busca, filtroTipo)}
                    ><FaChevronLeft /> Anterior</button>
                    <span className="inv-pag-info">Página {paginaAtual} de {totalPaginas} · {total} itens</span>
                    <button
                      className="inv-btn-ghost sm" disabled={paginaAtual >= totalPaginas}
                      onClick={() => carregarItens(tenant.id, offset + LIMIT, busca, filtroTipo)}
                    >Próxima <FaChevronRight /></button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 🧬 MODAL DE GESTÃO DE CATÁLOGO — tipos / status / categorias (admin_geral)
 * ========================================================================== */
const TIPO_FORM_VAZIO = { id: null, label: '', slug: '', icone: 'FaBoxOpen', cor: '#5b6b73', prefixo_patrimonio: 'INV', papel: 'nenhum', ordem: 100, ativo: 1, protegido: 0 };
const STATUS_FORM_VAZIO = { id: null, label: '', slug: '', cor: '#5b6b73', peso_saude: 0, zera_saude: 0, conta_em_uso: 0, eh_problema: 0, eh_estoque: 0, ordem: 100, ativo: 1, protegido: 0 };
const CAT_FORM_VAZIO = { id: null, nome: '', cor: '#0d7377', icone: 'FaCube', tipo_base: 'outro', descricao: '', ativo: 1 };

function CatalogoModal({ onClose, onChanged }) {
  const { confirm } = useDialogs();
  const [aba, setAba] = useState('tipos');
  const [cat, setCat] = useState({ tipos: [], status: [], categorias: [] });
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [formTipo, setFormTipo] = useState(TIPO_FORM_VAZIO);
  const [formStatus, setFormStatus] = useState(STATUS_FORM_VAZIO);
  const [formCat, setFormCat] = useState(CAT_FORM_VAZIO);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/inventario/catalogo');
      setCat(data);
    } catch { toast.error('Falha ao carregar o catálogo.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const aposMudar = async () => { await carregar(); onChanged?.(); };

  /* ---- TIPOS ---- */
  const salvarTipo = async () => {
    if (!formTipo.label.trim()) return toast.warn('Informe o label do tipo.');
    setSalvando(true);
    try {
      if (formTipo.id) await api.put(`/api/inventario/tipos/${formTipo.id}`, formTipo);
      else await api.post('/api/inventario/tipos', formTipo);
      toast.success('Tipo salvo.');
      setFormTipo(TIPO_FORM_VAZIO);
      aposMudar();
    } catch (e) { toast.error(e.response?.data?.error || 'Falha ao salvar tipo.'); }
    finally { setSalvando(false); }
  };
  const excluirTipo = async (t) => {
    if (!(await confirm({ titulo: 'Excluir tipo', mensagem: `Excluir o tipo "${t.label}"?`, confirmLabel: 'Excluir', perigo: true }))) return;
    try { await api.delete(`/api/inventario/tipos/${t.id}`); aposMudar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Falha ao excluir.'); }
  };

  /* ---- STATUS ---- */
  const salvarStatus = async () => {
    if (!formStatus.label.trim()) return toast.warn('Informe o label do status.');
    setSalvando(true);
    try {
      if (formStatus.id) await api.put(`/api/inventario/status/${formStatus.id}`, formStatus);
      else await api.post('/api/inventario/status', formStatus);
      toast.success('Status salvo.');
      setFormStatus(STATUS_FORM_VAZIO);
      aposMudar();
    } catch (e) { toast.error(e.response?.data?.error || 'Falha ao salvar status.'); }
    finally { setSalvando(false); }
  };
  const excluirStatus = async (s) => {
    if (!(await confirm({ titulo: 'Excluir status', mensagem: `Excluir o status "${s.label}"?`, confirmLabel: 'Excluir', perigo: true }))) return;
    try { await api.delete(`/api/inventario/status/${s.id}`); aposMudar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Falha ao excluir.'); }
  };

  /* ---- CATEGORIAS GLOBAIS ---- */
  const salvarCat = async () => {
    if (!formCat.nome.trim()) return toast.warn('Informe o nome da categoria.');
    setSalvando(true);
    try {
      if (formCat.id) await api.put(`/api/inventario/global/categorias/${formCat.id}`, formCat);
      else await api.post('/api/inventario/global/categorias', formCat);
      toast.success('Categoria salva.');
      setFormCat(CAT_FORM_VAZIO);
      aposMudar();
    } catch (e) { toast.error(e.response?.data?.error || 'Falha ao salvar categoria.'); }
    finally { setSalvando(false); }
  };
  const excluirCat = async (c) => {
    if (!(await confirm({ titulo: 'Excluir categoria', mensagem: `Excluir a categoria "${c.nome}"?`, confirmLabel: 'Excluir', perigo: true }))) return;
    try { await api.delete(`/api/inventario/global/categorias/${c.id}`); aposMudar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Falha ao excluir.'); }
  };

  const Toggle = ({ on, onClick, disabled }) => (
    <button type="button" className={`inv-toggle ${on ? 'on' : ''}`} onClick={onClick} disabled={disabled}>
      {on ? <FaToggleOn /> : <FaToggleOff />}
    </button>
  );

  return (
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal lg" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3><FaSlidersH /> Gestão do catálogo <span className="inv-badge st-reservado">global</span></h3>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <nav className="inv-drawer-tabs">
          <button className={aba === 'tipos' ? 'on' : ''} onClick={() => setAba('tipos')}><FaCube /> Tipos</button>
          <button className={aba === 'status' ? 'on' : ''} onClick={() => setAba('status')}><FaTags /> Status</button>
          <button className={aba === 'categorias' ? 'on' : ''} onClick={() => setAba('categorias')}><FaListUl /> Categorias</button>
        </nav>

        <div className="inv-modal-body">
          {loading ? (
            <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando catálogo…</div>
          ) : aba === 'tipos' ? (
            <div className="inv-cat-secao">
              <div className="inv-cat-form">
                <h4>{formTipo.id ? 'Editar tipo' : 'Novo tipo'} {formTipo.protegido ? <FaLock title="Canônico" /> : null}</h4>
                <div className="inv-form-grid">
                  <label>Label
                    <input value={formTipo.label} onChange={(e) => setFormTipo((f) => ({ ...f, label: e.target.value }))} />
                  </label>
                  <label>Slug {formTipo.protegido && <FaLock className="inv-lock-mini" />}
                    <input value={formTipo.slug} disabled={!!formTipo.protegido}
                      placeholder="auto a partir do label"
                      onChange={(e) => setFormTipo((f) => ({ ...f, slug: e.target.value }))} />
                  </label>
                  <label>Ícone
                    <select value={formTipo.icone} onChange={(e) => setFormTipo((f) => ({ ...f, icone: e.target.value }))}>
                      {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic.replace('Fa', '')}</option>)}
                    </select>
                  </label>
                  <label>Cor
                    <input type="color" value={formTipo.cor} onChange={(e) => setFormTipo((f) => ({ ...f, cor: e.target.value }))} />
                  </label>
                  <label>Prefixo patrimônio {formTipo.protegido && <FaLock className="inv-lock-mini" />}
                    <input value={formTipo.prefixo_patrimonio} disabled={!!formTipo.protegido} maxLength={8}
                      onChange={(e) => setFormTipo((f) => ({ ...f, prefixo_patrimonio: e.target.value.toUpperCase() }))} />
                  </label>
                  <label>Papel (comportamento) {formTipo.protegido && <FaLock className="inv-lock-mini" />}
                    <select value={formTipo.papel} disabled={!!formTipo.protegido}
                      onChange={(e) => setFormTipo((f) => ({ ...f, papel: e.target.value }))}>
                      {PAPEL_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </label>
                  <label>Ordem
                    <input type="number" value={formTipo.ordem} onChange={(e) => setFormTipo((f) => ({ ...f, ordem: e.target.value }))} />
                  </label>
                </div>
                <p className="inv-cat-dica">O <b>papel</b> é o que liga o tipo às features (saúde, reconciliação, kits). Em tipos canônicos ele fica travado.</p>
                <div className="inv-cat-form-acoes">
                  {formTipo.id && <button className="inv-btn-ghost sm" onClick={() => setFormTipo(TIPO_FORM_VAZIO)}>Cancelar</button>}
                  <button className="inv-btn sm" disabled={salvando} onClick={salvarTipo}>
                    {salvando ? <FaSpinner className="inv-spin" /> : <FaSave />} Salvar
                  </button>
                </div>
              </div>

              <table className="inv-table">
                <thead><tr><th>Tipo</th><th>Slug</th><th>Papel</th><th>Prefixo</th><th>Ativo</th><th></th></tr></thead>
                <tbody>
                  {cat.tipos.map((t) => {
                    const Icone = resolveIcon(t.icone);
                    return (
                      <tr key={t.id}>
                        <td><span className="inv-tipo-chip" style={{ color: t.cor, background: hexBg(t.cor) }}><Icone /> {t.label}</span></td>
                        <td className="mono">{t.slug} {t.protegido ? <FaLock className="inv-lock-mini" title="Canônico" /> : null}</td>
                        <td>{PAPEL_OPTIONS.find((p) => p.value === t.papel)?.label || t.papel}</td>
                        <td className="mono">{t.prefixo_patrimonio}</td>
                        <td><span className={`inv-dot-ativo ${t.ativo ? 'on' : ''}`} /></td>
                        <td className="inv-stk-acoes">
                          <button className="inv-btn-ghost sm" onClick={() => setFormTipo({ ...TIPO_FORM_VAZIO, ...t })}><FaPen /></button>
                          <button className="inv-btn-ghost sm danger" disabled={!!t.protegido} onClick={() => excluirTipo(t)}><FaTrashAlt /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : aba === 'status' ? (
            <div className="inv-cat-secao">
              <div className="inv-cat-form">
                <h4>{formStatus.id ? 'Editar status' : 'Novo status'} {formStatus.protegido ? <FaLock title="Canônico" /> : null}</h4>
                <div className="inv-form-grid">
                  <label>Label
                    <input value={formStatus.label} onChange={(e) => setFormStatus((f) => ({ ...f, label: e.target.value }))} />
                  </label>
                  <label>Slug {formStatus.protegido && <FaLock className="inv-lock-mini" />}
                    <input value={formStatus.slug} disabled={!!formStatus.protegido}
                      placeholder="auto a partir do label"
                      onChange={(e) => setFormStatus((f) => ({ ...f, slug: e.target.value }))} />
                  </label>
                  <label>Cor
                    <input type="color" value={formStatus.cor || '#5b6b73'} onChange={(e) => setFormStatus((f) => ({ ...f, cor: e.target.value }))} />
                  </label>
                  <label>Peso na saúde {formStatus.protegido && <FaLock className="inv-lock-mini" />}
                    <input type="number" min="0" max="100" value={formStatus.peso_saude} disabled={!!formStatus.protegido}
                      onChange={(e) => setFormStatus((f) => ({ ...f, peso_saude: e.target.value }))} />
                  </label>
                  <label>Ordem
                    <input type="number" value={formStatus.ordem} onChange={(e) => setFormStatus((f) => ({ ...f, ordem: e.target.value }))} />
                  </label>
                </div>
                <div className="inv-cat-flags">
                  <span className="inv-flag"><Toggle on={!!formStatus.conta_em_uso} disabled={!!formStatus.protegido} onClick={() => setFormStatus((f) => ({ ...f, conta_em_uso: f.conta_em_uso ? 0 : 1 }))} /> Conta como “em uso”</span>
                  <span className="inv-flag"><Toggle on={!!formStatus.eh_problema} disabled={!!formStatus.protegido} onClick={() => setFormStatus((f) => ({ ...f, eh_problema: f.eh_problema ? 0 : 1 }))} /> É problema</span>
                  <span className="inv-flag"><Toggle on={!!formStatus.eh_estoque} disabled={!!formStatus.protegido} onClick={() => setFormStatus((f) => ({ ...f, eh_estoque: f.eh_estoque ? 0 : 1 }))} /> Equivale a estoque</span>
                  <span className="inv-flag"><Toggle on={!!formStatus.zera_saude} disabled={!!formStatus.protegido} onClick={() => setFormStatus((f) => ({ ...f, zera_saude: f.zera_saude ? 0 : 1 }))} /> Terminal (zera saúde)</span>
                </div>
                <p className="inv-cat-dica">Pesos e flags governam o <b>health score</b> e a contagem de problemas. Em status canônicos, ficam travados.</p>
                <div className="inv-cat-form-acoes">
                  {formStatus.id && <button className="inv-btn-ghost sm" onClick={() => setFormStatus(STATUS_FORM_VAZIO)}>Cancelar</button>}
                  <button className="inv-btn sm" disabled={salvando} onClick={salvarStatus}>
                    {salvando ? <FaSpinner className="inv-spin" /> : <FaSave />} Salvar
                  </button>
                </div>
              </div>

              <table className="inv-table">
                <thead><tr><th>Status</th><th>Slug</th><th>Peso</th><th>Flags</th><th>Ativo</th><th></th></tr></thead>
                <tbody>
                  {cat.status.map((s) => (
                    <tr key={s.id}>
                      <td>{s.cor ? <span className="inv-badge" style={{ color: s.cor, background: hexBg(s.cor) }}>{s.label}</span> : <span className={`inv-badge ${s.classe || ''}`}>{s.label}</span>}</td>
                      <td className="mono">{s.slug} {s.protegido ? <FaLock className="inv-lock-mini" title="Canônico" /> : null}</td>
                      <td>{s.peso_saude}</td>
                      <td className="inv-flags-mini">
                        {s.conta_em_uso ? <span>em uso</span> : null}
                        {s.eh_problema ? <span className="warn">problema</span> : null}
                        {s.eh_estoque ? <span>estoque</span> : null}
                        {s.zera_saude ? <span className="danger">terminal</span> : null}
                      </td>
                      <td><span className={`inv-dot-ativo ${s.ativo ? 'on' : ''}`} /></td>
                      <td className="inv-stk-acoes">
                        <button className="inv-btn-ghost sm" onClick={() => setFormStatus({ ...STATUS_FORM_VAZIO, ...s })}><FaPen /></button>
                        <button className="inv-btn-ghost sm danger" disabled={!!s.protegido} onClick={() => excluirStatus(s)}><FaTrashAlt /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="inv-cat-secao">
              <div className="inv-cat-form">
                <h4>{formCat.id ? 'Editar categoria' : 'Nova categoria global'}</h4>
                <div className="inv-form-grid">
                  <label>Nome
                    <input value={formCat.nome} onChange={(e) => setFormCat((f) => ({ ...f, nome: e.target.value }))} />
                  </label>
                  <label>Cor
                    <input type="color" value={formCat.cor} onChange={(e) => setFormCat((f) => ({ ...f, cor: e.target.value }))} />
                  </label>
                  <label>Ícone
                    <select value={formCat.icone} onChange={(e) => setFormCat((f) => ({ ...f, icone: e.target.value }))}>
                      {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic.replace('Fa', '')}</option>)}
                    </select>
                  </label>
                  <label>Tipo base
                    <input value={formCat.tipo_base} onChange={(e) => setFormCat((f) => ({ ...f, tipo_base: e.target.value }))} placeholder="ex.: chip, rastreador, outro" />
                  </label>
                </div>
                <label className="inv-form-full">Descrição
                  <textarea rows="2" value={formCat.descricao} onChange={(e) => setFormCat((f) => ({ ...f, descricao: e.target.value }))} />
                </label>
                <div className="inv-cat-form-acoes">
                  {formCat.id && <button className="inv-btn-ghost sm" onClick={() => setFormCat(CAT_FORM_VAZIO)}>Cancelar</button>}
                  <button className="inv-btn sm" disabled={salvando} onClick={salvarCat}>
                    {salvando ? <FaSpinner className="inv-spin" /> : <FaSave />} Salvar
                  </button>
                </div>
              </div>

              <table className="inv-table">
                <thead><tr><th>Categoria</th><th>Tipo base</th><th>Ativo</th><th></th></tr></thead>
                <tbody>
                  {cat.categorias.map((c) => (
                    <tr key={c.id}>
                      <td><span className="inv-tag" style={{ '--c': c.cor }}>{c.nome}</span></td>
                      <td>{c.tipo_base || '—'}</td>
                      <td><span className={`inv-dot-ativo ${c.ativo ? 'on' : ''}`} /></td>
                      <td className="inv-stk-acoes">
                        <button className="inv-btn-ghost sm" onClick={() => setFormCat({ ...CAT_FORM_VAZIO, ...c })}><FaPen /></button>
                        <button className="inv-btn-ghost sm danger" onClick={() => excluirCat(c)}><FaTrashAlt /></button>
                      </td>
                    </tr>
                  ))}
                  {!cat.categorias.length && <tr><td colSpan="4" className="inv-vazio-sec">Nenhuma categoria global. As por-tenant existentes continuam valendo.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 📝 MODAL DE ITEM (criar/editar) — campos por PAPEL
 * ========================================================================== */
const FORM_VAZIO = {
  tipo: '', nome: '', status: '', categoria_id: '',
  numero_serie: '', fabricante: '', modelo: '',
  iccid: '', numero_linha: '', operadora: '', plano_dados: '', franquia_mb: '', custo_mensal: '',
  imei: '', protocolo: '', firmware: '',
  valor_aquisicao: '', data_aquisicao: '', vida_util_meses: '', nota_fiscal_numero: '', garantia_ate: '',
  localizacao: '', observacoes: '',
};

function ItemModal({ meta, item, categorias, onClose, onSalvo }) {
  const tipoPadrao = meta.tipos.find((t) => t.papel === 'rastreador')?.slug || meta.tipos[0]?.slug || '';
  const statusPadrao = meta.status.find((s) => s.eh_estoque)?.slug || meta.status[0]?.slug || '';
  const [form, setForm] = useState({ ...FORM_VAZIO, tipo: tipoPadrao, status: statusPadrao });
  const [salvando, setSalvando] = useState(false);
  const editando = !!item;

  useEffect(() => {
    if (item) {
      setForm({
        ...FORM_VAZIO, ...item,
        categoria_id: item.categoria_id || '',
        data_aquisicao: item.data_aquisicao ? String(item.data_aquisicao).slice(0, 10) : '',
        garantia_ate: item.garantia_ate ? String(item.garantia_ate).slice(0, 10) : '',
      });
    } else {
      setForm({ ...FORM_VAZIO, tipo: tipoPadrao, status: statusPadrao });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const papel = meta.tiposBySlug[form.tipo]?.papel;

  const salvar = async () => {
    setSalvando(true);
    try {
      if (editando) {
        await api.put(`/api/inventario/itens/${item.id}`, form);
        toast.success('Item atualizado.');
      } else {
        await api.post('/api/inventario/itens', form);
        toast.success('Item cadastrado.');
      }
      onSalvo();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao salvar.');
    } finally { setSalvando(false); }
  };

  return (
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3>{editando ? `Editar ${form.codigo_patrimonio || 'item'}` : 'Novo item'}</h3>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="inv-modal-body">
          <div className="inv-form-grid">
            <label>Tipo
              <select value={form.tipo} onChange={set('tipo')} disabled={editando}>
                {meta.tipos.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </label>
            <label>Categoria
              <select value={form.categoria_id} onChange={set('categoria_id')}>
                <option value="">— sem categoria —</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.global ? ' (global)' : ''}</option>)}
              </select>
            </label>
            <label>Status
              <select value={form.status} onChange={set('status')}>
                {meta.status.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </label>
            <label>Apelido / nome<input value={form.nome || ''} onChange={set('nome')} placeholder="Opcional" /></label>
            <label>Fabricante<input value={form.fabricante || ''} onChange={set('fabricante')} /></label>
            <label>Modelo<input value={form.modelo || ''} onChange={set('modelo')} /></label>
            <label>Nº de série<input value={form.numero_serie || ''} onChange={set('numero_serie')} /></label>

            {papel === 'chip' && (
              <>
                <label>ICCID<input value={form.iccid || ''} onChange={set('iccid')} className="mono-input" /></label>
                <label>Linha<input value={form.numero_linha || ''} onChange={set('numero_linha')} /></label>
                <label>Operadora<input value={form.operadora || ''} onChange={set('operadora')} /></label>
                <label>Plano de dados<input value={form.plano_dados || ''} onChange={set('plano_dados')} /></label>
                <label>Franquia (MB)<input type="number" value={form.franquia_mb || ''} onChange={set('franquia_mb')} /></label>
                <label>Custo mensal (R$)<input type="number" step="0.01" value={form.custo_mensal || ''} onChange={set('custo_mensal')} /></label>
              </>
            )}
            {papel === 'rastreador' && (
              <>
                <label>IMEI<input value={form.imei || ''} onChange={set('imei')} className="mono-input" /></label>
                <label>Protocolo<input value={form.protocolo || ''} onChange={set('protocolo')} placeholder="ex.: GT06" /></label>
                <label>Firmware<input value={form.firmware || ''} onChange={set('firmware')} /></label>
              </>
            )}

            <label>Localização<input value={form.localizacao || ''} onChange={set('localizacao')} /></label>
            <label>Valor de aquisição (R$)<input type="number" step="0.01" value={form.valor_aquisicao || ''} onChange={set('valor_aquisicao')} /></label>
            <label>Data de aquisição<input type="date" value={form.data_aquisicao || ''} onChange={set('data_aquisicao')} /></label>
            <label>Vida útil (meses)<input type="number" value={form.vida_util_meses || ''} onChange={set('vida_util_meses')} /></label>
            <label>Nota fiscal nº<input value={form.nota_fiscal_numero || ''} onChange={set('nota_fiscal_numero')} /></label>
            <label>Garantia até<input type="date" value={form.garantia_ate || ''} onChange={set('garantia_ate')} /></label>
          </div>
          <label className="inv-form-full">Observações
            <textarea rows="2" value={form.observacoes || ''} onChange={set('observacoes')} />
          </label>
        </div>

        <div className="inv-modal-foot">
          <button className="inv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="inv-btn" disabled={salvando} onClick={salvar}>
            {salvando ? <FaSpinner className="inv-spin" /> : null} {editando ? 'Salvar alterações' : 'Cadastrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 🔗 MODAL DE VÍNCULO
 * ========================================================================== */
function VinculoModal({ meta, origem, candidatos, onClose, onVinculado }) {
  const [alvo, setAlvo] = useState('');
  const ehChip = meta.tiposBySlug[origem.tipo]?.papel === 'chip';

  const vincular = async () => {
    if (!alvo) return;
    const paiId = ehChip ? Number(alvo) : origem.id;
    const filhoId = ehChip ? origem.id : Number(alvo);
    try {
      await api.post('/api/inventario/vinculos', { item_pai_id: paiId, item_filho_id: filhoId });
      toast.success('Vínculo criado.');
      onVinculado();
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Falha ao vincular.'); }
  };

  return (
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal sm" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3>Vincular {origem.codigo_patrimonio}</h3>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="inv-modal-body">
          <div className="inv-vinc-fluxo">
            <span className="inv-chip-vis" style={{ color: meta.tiposBySlug[origem.tipo]?.cor, background: hexBg(meta.tiposBySlug[origem.tipo]?.cor) }}>
              <TipoChip meta={meta} tipo={origem.tipo} mini /> {origem.codigo_patrimonio}
            </span>
            <FaArrowRight className="inv-vinc-seta" />
            <select value={alvo} onChange={(e) => setAlvo(e.target.value)}>
              <option value="">Selecione um {ehChip ? 'rastreador' : 'chip'}…</option>
              {candidatos.map((c) => (
                <option key={c.id} value={c.id}>{c.codigo_patrimonio} {c.nome ? `· ${c.nome}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="inv-modal-foot">
          <button className="inv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="inv-btn" disabled={!alvo} onClick={vincular}><FaLink /> Vincular</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 🔄 MODAL DE CUSTÓDIA — seletor de usuários reais do sistema (routesUsers)
 * Substitui o prompt nativo "ID do novo responsável" por uma lista buscável.
 * ========================================================================== */
function CustodiaModal({ itemId, atualResponsavelId, onClose, onConcluido }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // /selecao já volta filtrado por tenant: { id, nome, email, perfil }
        const { data } = await api.get(`${API_USUARIOS}/selecao`);
        setUsuarios(Array.isArray(data) ? data : []);
      } catch { toast.error('Falha ao carregar usuários do sistema.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter((u) => `${u.nome || ''} ${u.email || ''}`.toLowerCase().includes(q));
  }, [usuarios, busca]);

  const iniciais = (nome) =>
    (nome || '').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

  const confirmar = async () => {
    if (!sel) return;
    setSalvando(true);
    try {
      await api.post(`/api/inventario/itens/${itemId}/custodia`, { responsavel_id: sel.id });
      toast.success(`Custódia transferida para ${sel.nome}.`);
      onConcluido();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao transferir custódia.');
    } finally { setSalvando(false); }
  };

  return (
    <div className="inv-modal-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="inv-modal-head">
          <h3><FaExchangeAlt /> Transferir custódia</h3>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="inv-modal-body">
          <div className="inv-busca inv-busca-full">
            <FaSearch />
            <input
              autoFocus placeholder="Buscar por nome ou e-mail…"
              value={busca} onChange={(e) => setBusca(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando usuários…</div>
          ) : !filtrados.length ? (
            <div className="inv-empty"><FaUser className="inv-empty-icon" /><p>Nenhum usuário encontrado.</p></div>
          ) : (
            <ul className="inv-user-lista">
              {filtrados.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className={`inv-user-card ${sel?.id === u.id ? 'sel' : ''}`}
                    onClick={() => setSel(u)}
                  >
                    <span className="inv-user-avatar">{iniciais(u.nome)}</span>
                    <span className="inv-user-info">
                      <strong>
                        {u.nome}
                        {u.id === atualResponsavelId && <em className="inv-user-atual"> · responsável atual</em>}
                      </strong>
                      <span className="muted">{u.email || '—'}</span>
                    </span>
                    {u.perfil && <span className="inv-user-perfil">{u.perfil}</span>}
                    {sel?.id === u.id && <FaCheck className="inv-user-check" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="inv-modal-foot">
          <button className="inv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="inv-btn" disabled={!sel || salvando} onClick={confirmar}>
            {salvando ? <FaSpinner className="inv-spin" /> : <FaExchangeAlt />}
            {sel ? ` Transferir para ${sel.nome.split(' ')[0]}` : ' Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * 🗂️ DRAWER DE DETALHE — com upload de NF e etiqueta de tenant
 * ========================================================================== */
function ItemDrawer({ meta, itemId, podeManipular, ehAdminGeral, onClose, onEditar, onMudou }) {
  const { confirm, prompt } = useDialogs();
  const [item, setItem] = useState(null);
  const [aba, setAba] = useState('geral');
  const [candidatos, setCandidatos] = useState([]);
  const [vincOpen, setVincOpen] = useState(false);
  const [custodiaOpen, setCustodiaOpen] = useState(false);
  const [enviandoNF, setEnviandoNF] = useState(false);
  const inputNFRef = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/inventario/itens/${itemId}`);
      setItem(data);
    } catch { toast.error('Falha ao carregar item.'); onClose(); }
  }, [itemId, onClose]);
  useEffect(() => { carregar(); }, [carregar]);

  const papel = item ? meta.tiposBySlug[item.tipo]?.papel : null;

  const abrirVinculo = async () => {
    const tipoAlvoPapel = papel === 'chip' ? 'rastreador' : 'chip';
    const slugsAlvo = meta.tipos.filter((t) => t.papel === tipoAlvoPapel).map((t) => t.slug);
    try {
      // busca candidatos sem vínculo de qualquer tipo com o papel oposto
      const buscas = await Promise.all(
        slugsAlvo.map((slug) => api.get(`/api/inventario/itens?tipo=${slug}&vinculo=sem&limit=200`).then((r) => r.data))
      );
      setCandidatos(buscas.flat());
      setVincOpen(true);
    } catch { toast.error('Falha ao buscar candidatos.'); }
  };

  const desvincular = async (vid) => {
    try { await api.delete(`/api/inventario/vinculos/${vid}`); carregar(); onMudou(); }
    catch { toast.error('Falha ao desvincular.'); }
  };

  const excluir = async () => {
    if (!(await confirm({
      titulo: 'Dar baixa no item',
      mensagem: 'O item será baixado do inventário (soft-delete). Deseja continuar?',
      confirmLabel: 'Baixar', perigo: true,
    }))) return;
    try { await api.delete(`/api/inventario/itens/${itemId}`); toast.success('Item baixado.'); onMudou(); onClose(); }
    catch { toast.error('Falha ao excluir.'); }
  };

  // 📎 Upload real de NF → Drive
  const escolherNF = () => inputNFRef.current?.click();
  const enviarNF = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // permite reanexar o mesmo arquivo
    if (!arquivo) return;
    const numero = await prompt({
      titulo: 'Anexar nota fiscal',
      mensagem: `Arquivo: ${arquivo.name}. Informe o número da NF (opcional).`,
      placeholder: 'ex.: NF-1001',
      confirmLabel: 'Anexar',
    });
    if (numero === null) return; // cancelou
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('tipo', 'nota_fiscal');
    if (numero) fd.append('nota_fiscal_numero', numero);
    setEnviandoNF(true);
    try {
      // NÃO setar Content-Type manualmente: o axios injeta o boundary do multipart
      await api.post(`/api/inventario/itens/${itemId}/anexos/upload`, fd);
      toast.success('NF anexada.');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao anexar NF.');
    } finally { setEnviandoNF(false); }
  };

  if (!item) {
    return (
      <div className="inv-drawer-overlay" onClick={onClose}>
        <aside className="inv-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando…</div>
        </aside>
      </div>
    );
  }

  const tm = meta.tiposBySlug[item.tipo] || {};
  const Icone = tm.Icone || FaBoxOpen;

  return (
    <div className="inv-drawer-overlay" onClick={onClose}>
      <aside className="inv-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="inv-drawer-head">
          <div className="inv-drawer-ic" style={{ color: tm.cor, background: hexBg(tm.cor) }}><Icone /></div>
          <div className="inv-drawer-titulo">
            <span className="mono inv-drawer-cod">{item.codigo_patrimonio}</span>
            <strong>{item.nome || tm.label}</strong>
            {ehAdminGeral && <TenantTag nome={item.tenant_nome} />}
          </div>
          <button className="inv-icon-btn" onClick={onClose}><FaTimes /></button>
        </header>

        <input
          ref={inputNFRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={enviarNF}
        />

        <div className="inv-drawer-resumo">
          <HealthGauge saude={item.saude} />
          <div className="inv-drawer-resumo-meta">
            <StatusBadge meta={meta} status={item.status} />
            {item.categoria_nome && <span className="inv-tag" style={{ '--c': item.categoria_cor }}>{item.categoria_nome}</span>}
            {item.saude?.fatores?.length > 0 && (
              <ul className="inv-fatores">
                {item.saude.fatores.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </div>
        </div>

        {podeManipular && (
          <div className="inv-drawer-acoes">
            <button className="inv-btn-ghost sm" onClick={() => onEditar(item)}><FaPen /> Editar</button>
            {(papel === 'chip' || papel === 'rastreador') && (
              <button className="inv-btn-ghost sm" onClick={abrirVinculo}><FaLink /> Vincular</button>
            )}
            <button className="inv-btn-ghost sm" onClick={() => setCustodiaOpen(true)}><FaExchangeAlt /> Custódia</button>
            <button className="inv-btn-ghost sm" onClick={escolherNF} disabled={enviandoNF}>
              {enviandoNF ? <FaSpinner className="inv-spin" /> : <FaFileInvoiceDollar />} Anexar NF
            </button>
            <button className="inv-btn-ghost sm danger" onClick={excluir}><FaTrashAlt /> Baixar</button>
          </div>
        )}

        <nav className="inv-drawer-tabs">
          {['geral', 'vinculos', 'historico', 'anexos'].map((t) => (
            <button key={t} className={aba === t ? 'on' : ''} onClick={() => setAba(t)}>
              {t === 'geral' && 'Visão geral'}
              {t === 'vinculos' && `Vínculos (${item.vinculos?.length || 0})`}
              {t === 'historico' && 'Histórico'}
              {t === 'anexos' && `Anexos (${item.anexos?.length || 0})`}
            </button>
          ))}
        </nav>

        <div className="inv-drawer-conteudo">
          {aba === 'geral' && (
            <dl className="inv-dl">
              {papel === 'chip' && <>
                <div><dt>ICCID</dt><dd className="mono">{item.iccid || '—'}</dd></div>
                <div><dt>Linha</dt><dd>{item.numero_linha || '—'}</dd></div>
                <div><dt>Operadora</dt><dd>{item.operadora || '—'}</dd></div>
                <div><dt>Plano</dt><dd>{item.plano_dados || '—'}</dd></div>
                <div><dt>Custo mensal</dt><dd>{item.custo_mensal ? moeda(item.custo_mensal) : '—'}</dd></div>
              </>}
              {papel === 'rastreador' && <>
                <div><dt>IMEI</dt><dd className="mono">{item.imei || '—'}</dd></div>
                <div><dt>Protocolo</dt><dd>{item.protocolo || '—'}</dd></div>
                <div><dt>Firmware</dt><dd>{item.firmware || '—'}</dd></div>
                <div><dt>Último sinal</dt><dd>{dataHoraBR(item.ultimo_sinal_em)}</dd></div>
              </>}
              <div><dt>Fabricante</dt><dd>{item.fabricante || '—'}</dd></div>
              <div><dt>Modelo</dt><dd>{item.modelo || '—'}</dd></div>
              <div><dt>Nº série</dt><dd className="mono">{item.numero_serie || '—'}</dd></div>
              <div><dt>Localização</dt><dd>{item.localizacao || '—'}</dd></div>
              <div><dt>Veículo</dt><dd>{item.veiculo_id ? `#${item.veiculo_id}` : '—'}</dd></div>
              <div><dt>Valor</dt><dd>{item.valor_aquisicao ? moeda(item.valor_aquisicao) : '—'}</dd></div>
              <div><dt>Aquisição</dt><dd>{dataBR(item.data_aquisicao)}</dd></div>
              <div><dt>Garantia até</dt><dd>{dataBR(item.garantia_ate)}</dd></div>
              <div><dt>Nota fiscal</dt><dd>{item.nota_fiscal_numero || '—'}</dd></div>
              {item.observacoes && <div className="full"><dt>Observações</dt><dd>{item.observacoes}</dd></div>}
            </dl>
          )}

          {aba === 'vinculos' && (
            item.vinculos?.length ? (
              <ul className="inv-vinc-lista">
                {item.vinculos.map((v) => {
                  const ehPai = v.item_pai_id === item.id;
                  const outro = ehPai
                    ? { cod: v.filho_codigo, nome: v.filho_nome, tipo: v.filho_tipo }
                    : { cod: v.pai_codigo, nome: v.pai_nome, tipo: v.pai_tipo };
                  return (
                    <li key={v.id}>
                      <span className="inv-chip-vis" style={{ color: meta.tiposBySlug[outro.tipo]?.cor, background: hexBg(meta.tiposBySlug[outro.tipo]?.cor) }}>
                        <TipoChip meta={meta} tipo={outro.tipo} mini /> {outro.cod}
                      </span>
                      <span className="inv-vinc-rel">{ehPai ? 'contém →' : '← pertence a'} {v.tipo_relacao}</span>
                      {podeManipular && (
                        <button className="inv-icon-btn danger" title="Desvincular" onClick={() => desvincular(v.id)}>
                          <FaUnlink />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : <p className="inv-vazio-sec">Sem vínculos ativos.</p>
          )}

          {aba === 'historico' && (
            item.historico?.length ? (
              <ul className="inv-timeline">
                {item.historico.map((h) => (
                  <li key={h.id}>
                    <span className="inv-timeline-dot" />
                    <div className="inv-timeline-corpo">
                      <strong>{h.tipo}</strong>
                      <span className="inv-timeline-det">
                        {h.de_valor && <>de <em>{h.de_valor}</em> </>}
                        {h.para_valor && <>para <em>{h.para_valor}</em></>}
                        {h.observacao}
                      </span>
                      <span className="inv-timeline-meta">{h.usuario_nome || '—'} · {dataHoraBR(h.created_at)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p className="inv-vazio-sec">Sem histórico.</p>
          )}

          {aba === 'anexos' && (
            item.anexos?.length ? (
              <ul className="inv-anexos">
                {item.anexos.map((a) => (
                  <li key={a.id}>
                    <FaPaperclip />
                    <a href={a.url} target="_blank" rel="noreferrer">{a.nome}</a>
                    <span className="inv-tag-mini">{a.tipo}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="inv-vazio-sec">Nenhum anexo. Use “Anexar NF” acima.</p>
          )}
        </div>

        {vincOpen && (
          <VinculoModal
            meta={meta}
            origem={item}
            candidatos={candidatos}
            onClose={() => setVincOpen(false)}
            onVinculado={() => { carregar(); onMudou(); }}
          />
        )}

        {custodiaOpen && (
          <CustodiaModal
            itemId={itemId}
            atualResponsavelId={item.responsavel_id}
            onClose={() => setCustodiaOpen(false)}
            onConcluido={() => { carregar(); onMudou(); }}
          />
        )}
      </aside>
    </div>
  );
}

/* ============================================================================
 * 🧭 COMPONENTE PRINCIPAL
 * ========================================================================== */
function InventarioCMDBInner({ user }) {
  const podeManipular = useMemo(() => PODE_MANIPULAR.includes(user?.perfil), [user]);
  const ehAdminGeral = user?.perfil === 'admin_geral';

  const [aba, setAba] = useState('inventario');
  const [meta, setMeta] = useState(META_VAZIO);
  const [metaPronta, setMetaPronta] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [itens, setItens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ q: '', tipo: '', status: '', categoria_id: '' });

  const [drawerId, setDrawerId] = useState(null);
  const [modalItem, setModalItem] = useState(undefined); // undefined=fechado, null=novo, obj=editar
  const [multiOpen, setMultiOpen] = useState(false);
  const [catalogoOpen, setCatalogoOpen] = useState(false);

  // import/export (admin escolhe tenant de destino)
  const [tenants, setTenants] = useState([]);
  const [tenantAlvo, setTenantAlvo] = useState('');
  const [importando, setImportando] = useState(false);
  const inputImportRef = useRef(null);

  const carregarMeta = useCallback(async () => {
    try {
      const [{ data: tipos }, { data: status }] = await Promise.all([
        api.get('/api/inventario/tipos'),
        api.get('/api/inventario/status'),
      ]);
      setMeta(construirMeta(tipos, status));
    } catch { toast.error('Falha ao carregar tipos/status.'); }
    finally { setMetaPronta(true); }
  }, []);

  const carregarResumo = useCallback(async () => {
    try { const { data } = await api.get('/api/inventario/resumo'); setResumo(data); } catch {}
  }, []);
  const carregarCategorias = useCallback(async () => {
    try { const { data } = await api.get('/api/inventario/categorias'); setCategorias(data); } catch {}
  }, []);

  const carregarItens = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.q) params.set('q', filtros.q);
      if (filtros.tipo) params.set('tipo', filtros.tipo);
      if (filtros.status) params.set('status', filtros.status);
      if (filtros.categoria_id) params.set('categoria_id', filtros.categoria_id);
      const { data } = await api.get(`/api/inventario/itens?${params.toString()}`);
      setItens(data);
    } catch { toast.error('Falha ao carregar itens.'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregarMeta(); carregarResumo(); carregarCategorias(); }, [carregarMeta, carregarResumo, carregarCategorias]);
  useEffect(() => {
    if (ehAdminGeral) {
      api.get('/api/inventario/global/tenants').then(({ data }) => setTenants(data)).catch(() => {});
    }
  }, [ehAdminGeral]);
  useEffect(() => {
    if (aba === 'inventario') {
      const t = setTimeout(carregarItens, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [aba, carregarItens]);

  const recarregarTudo = () => { carregarItens(); carregarResumo(); };

  /* ---- IMPORT / EXPORT (SheetJS) ---- */
  const baixarModelo = () => {
    const exemplo = [
      { codigo_patrimonio: '', tipo: 'rastreador', nome: 'Ex.: Rastreador veículo 12', status: 'em_estoque',
        imei: '860000000000001', protocolo: 'GT06', fabricante: 'Concox', modelo: 'GT06N', numero_serie: '',
        valor_aquisicao: 120.5, data_aquisicao: '2025-01-15', nota_fiscal_numero: 'NF-1001', custo_mensal: '' },
      { codigo_patrimonio: '', tipo: 'chip', nome: 'Ex.: Chip M2M Vivo', status: 'em_estoque',
        iccid: '8955000000000000001', numero_linha: '11999990000', operadora: 'Vivo', plano_dados: 'M2M 50MB',
        valor_aquisicao: 0, data_aquisicao: '', nota_fiscal_numero: '', custo_mensal: 9.9 },
    ];
    const ws = XLSX.utils.json_to_sheet(exemplo, { header: COLUNAS_EXPORT });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Itens');
    XLSX.writeFile(wb, 'modelo_inventario.xlsx');
  };

  const fetchTodosItens = async () => {
    const todos = [];
    const limit = 200;
    let offset = 0;
    const usaGlobal = ehAdminGeral && tenantAlvo;
    for (let i = 0; i < 100; i++) {
      const params = new URLSearchParams();
      params.set('limit', usaGlobal ? limit : Math.min(limit, 500));
      params.set('offset', offset);
      if (usaGlobal) params.set('tenant_id', tenantAlvo);
      const base = usaGlobal ? '/api/inventario/global/itens' : '/api/inventario/itens';
      const { data } = await api.get(`${base}?${params.toString()}`);
      const lote = Array.isArray(data) ? data : (data.itens || []);
      todos.push(...lote);
      offset += lote.length || limit;
      const acabou = Array.isArray(data) ? lote.length < (usaGlobal ? limit : 500) : offset >= (data.total || 0);
      if (acabou || lote.length === 0) break;
    }
    return todos;
  };

  const exportar = async () => {
    try {
      toast.info('Gerando planilha…');
      const dados = await fetchTodosItens();
      if (!dados.length) return toast.warn('Nada para exportar.');
      const cols = ehAdminGeral && tenantAlvo ? ['tenant_nome', ...COLUNAS_EXPORT] : COLUNAS_EXPORT;
      const linhas = dados.map((it) => {
        const row = {};
        cols.forEach((c) => { row[c] = it[c] ?? ''; });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(linhas, { header: cols });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
      const sufixo = ehAdminGeral && tenantAlvo
        ? `_tenant_${tenants.find((t) => String(t.id) === String(tenantAlvo))?.nome_empresa || tenantAlvo}`
        : '';
      XLSX.writeFile(wb, `inventario${sufixo}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { toast.error('Falha ao exportar.'); }
  };

  const escolherImport = () => inputImportRef.current?.click();
  const importar = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    setImportando(true);
    try {
      const buf = await arquivo.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const linhas = XLSX.utils.sheet_to_json(ws, { defval: '' })
        .map((r) => {
          const obj = {};
          Object.keys(r).forEach((k) => { obj[String(k).trim()] = typeof r[k] === 'string' ? r[k].trim() : r[k]; });
          return obj;
        })
        .filter((r) => r.tipo);
      if (!linhas.length) { toast.warn('Planilha sem linhas válidas (coluna "tipo" obrigatória).'); return; }

      let data;
      if (ehAdminGeral && tenantAlvo) {
        ({ data } = await api.post('/api/inventario/global/importar', { tenant_id: Number(tenantAlvo), itens: linhas }));
      } else {
        ({ data } = await api.post('/api/inventario/itens/importar', { itens: linhas }));
      }
      toast.success(`Importação: ${data.criados} criados, ${data.erros.length} erro(s).`);
      recarregarTudo();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha na importação (verifique o arquivo).');
    } finally { setImportando(false); }
  };

  const ABAS = [
    { id: 'inventario', label: 'Inventário', icon: <FaBoxOpen /> },
    { id: 'topologia', label: 'Topologia', icon: <FaProjectDiagram /> },
    { id: 'alertas', label: 'Alertas', icon: <FaBell /> },
    { id: 'reconciliacao', label: 'Reconciliação', icon: <FaGhost /> },
    { id: 'kits', label: 'Kits', icon: <FaLayerGroup /> },
    { id: 'stocktake', label: 'Inventário físico', icon: <FaClipboardCheck /> },
  ];

  const t = resumo?.totais || {};

  if (!metaPronta) {
    return <div className="inv-root"><div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando inventário…</div></div>;
  }

  return (
    <div className="inv-root">
      {/* Cabeçalho */}
      <header className="inv-header">
        <div className="inv-header-titulo">
          <FaSatelliteDish className="inv-header-ic" />
          <div>
            <h1>Inventário de Itens</h1>
            <p>Chips e rastreadores da central — catalogação, vínculos e saúde dos ativos</p>
          </div>
        </div>
        {ehAdminGeral && (
          <div className="inv-header-acoes">
            <button className="inv-btn-ghost" onClick={() => setCatalogoOpen(true)}>
              <FaSlidersH /> Gerenciar catálogo
            </button>
            <button className="inv-btn-multitenant" onClick={() => setMultiOpen(true)}>
              <FaGlobeAmericas /> Visão Multi-Tenant
            </button>
          </div>
        )}
      </header>

      {/* KPIs */}
      <div className="inv-kpis">
        <KpiCard icon={<FaCube />} valor={t.total || 0} label="Itens ativos" />
        <KpiCard icon={<FaMicrochip />} valor={t.chips || 0} label="Chips" accent="indigo" />
        <KpiCard icon={<FaSatelliteDish />} valor={t.rastreadores || 0} label="Rastreadores" accent="teal" />
        <KpiCard icon={<FaCar />} valor={t.em_uso || 0} label="Em uso" />
        <KpiCard icon={<FaExclamationTriangle />} valor={t.problemas || 0} label="Com problema" accent="warn" />
        <KpiCard icon={<FaFileInvoiceDollar />} valor={moeda(t.valor_total)} label="Valor total" />
      </div>

      {/* Abas */}
      <nav className="inv-tabs">
        {ABAS.map((a) => (
          <button key={a.id} className={aba === a.id ? 'on' : ''} onClick={() => setAba(a.id)}>
            {a.icon} {a.label}
          </button>
        ))}
      </nav>

      {/* Conteúdo */}
      <main className="inv-main">
        {aba === 'inventario' && (
          <>
            <div className="inv-toolbar">
              <div className="inv-busca">
                <FaSearch />
                <input
                  placeholder="Buscar por patrimônio, IMEI, ICCID, linha, série…"
                  value={filtros.q}
                  onChange={(e) => setFiltros((f) => ({ ...f, q: e.target.value }))}
                />
              </div>
              <select value={filtros.tipo} onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))}>
                <option value="">Todos os tipos</option>
                {meta.tipos.map((tp) => <option key={tp.slug} value={tp.slug}>{tp.label}</option>)}
              </select>
              <select value={filtros.status} onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
                <option value="">Todos os status</option>
                {meta.status.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>

              {podeManipular && (
                <div className="inv-toolbar-acoes">
                  {ehAdminGeral && (
                    <select className="inv-tenant-select" value={tenantAlvo} onChange={(e) => setTenantAlvo(e.target.value)} title="Tenant para exportar/importar">
                      <option value="">Meu tenant</option>
                      {tenants.map((tt) => <option key={tt.id} value={tt.id}>{tt.nome_empresa}</option>)}
                    </select>
                  )}
                  <button className="inv-btn-ghost" onClick={baixarModelo}><FaFileDownload /> Modelo</button>
                  <button className="inv-btn-ghost" onClick={exportar}><FaFileExport /> Exportar</button>
                  <button className="inv-btn-ghost" onClick={escolherImport} disabled={importando}>
                    {importando ? <FaSpinner className="inv-spin" /> : <FaFileImport />} Importar
                  </button>
                  <input ref={inputImportRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={importar} />
                  <button className="inv-btn" onClick={() => setModalItem(null)}><FaPlus /> Novo item</button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="inv-loading"><FaSpinner className="inv-spin" /> Carregando inventário…</div>
            ) : !itens.length ? (
              <div className="inv-empty">
                <FaBoxOpen className="inv-empty-icon" />
                <p>Nenhum item encontrado. {podeManipular && 'Comece cadastrando um chip ou rastreador.'}</p>
              </div>
            ) : (
              <div className="inv-tabela-wrap">
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>Saúde</th><th>Patrimônio</th>{ehAdminGeral && <th>Tenant</th>}<th>Tipo</th><th>Identificador</th>
                      <th>Status</th><th>Vínculos</th><th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it) => {
                      const papel = meta.tiposBySlug[it.tipo]?.papel;
                      return (
                        <tr key={it.id} className="inv-row" onClick={() => setDrawerId(it.id)}>
                          <td>
                            <span className={`inv-saude-pill ${SAUDE_META[it.saude?.nivel]?.classe}`}>
                              <FaHeartbeat /> {it.saude?.score ?? '—'}
                            </span>
                          </td>
                          <td className="mono">{it.codigo_patrimonio}</td>
                          {ehAdminGeral && <td><TenantTag nome={it.tenant_nome} /></td>}
                          <td><TipoChip meta={meta} tipo={it.tipo} /></td>
                          <td className="mono inv-ident">{papel === 'chip' ? (it.iccid || it.numero_linha || '—') : (it.imei || '—')}</td>
                          <td><StatusBadge meta={meta} status={it.status} /></td>
                          <td>{it.vinculos_ativos > 0 ? <span className="inv-vinc-count"><FaLink /> {it.vinculos_ativos}</span> : <span className="muted">—</span>}</td>
                          <td>{it.valor_aquisicao ? moeda(it.valor_aquisicao) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {aba === 'topologia' && <TopologyView meta={meta} onAbrirItem={setDrawerId} />}
        {aba === 'alertas' && <AlertasView onAbrirItem={setDrawerId} podeManipular={podeManipular} />}
        {aba === 'reconciliacao' && <ReconciliacaoView onAbrirItem={setDrawerId} />}
        {aba === 'kits' && <KitsView podeManipular={podeManipular} />}
        {aba === 'stocktake' && <StocktakeView meta={meta} podeManipular={podeManipular} />}
      </main>

      {/* Drawer de detalhe */}
      {drawerId && (
        <ItemDrawer
          meta={meta}
          itemId={drawerId}
          podeManipular={podeManipular}
          ehAdminGeral={ehAdminGeral}
          onClose={() => setDrawerId(null)}
          onEditar={(it) => { setModalItem(it); }}
          onMudou={recarregarTudo}
        />
      )}

      {/* Modal criar/editar */}
      {modalItem !== undefined && (
        <ItemModal
          meta={meta}
          item={modalItem}
          categorias={categorias}
          onClose={() => setModalItem(undefined)}
          onSalvo={recarregarTudo}
        />
      )}

      {/* Modal Multi-Tenant */}
      {multiOpen && <MultiTenantModal meta={meta} onClose={() => setMultiOpen(false)} />}

      {/* Modal de gestão de catálogo */}
      {catalogoOpen && (
        <CatalogoModal
          onClose={() => setCatalogoOpen(false)}
          onChanged={() => { carregarMeta(); carregarCategorias(); carregarResumo(); }}
        />
      )}
    </div>
  );
}

/* ============================================================================
 * Export — envolve tudo no DialogProvider para que confirm/prompt estejam
 * disponíveis em qualquer subcomponente via useDialogs().
 * ========================================================================== */
export default function InventarioCMDB({ user }) {
  return (
    <DialogProvider>
      <InventarioCMDBInner user={user} />
    </DialogProvider>
  );
}